import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrganizationContextService } from './organization-context.service';
import { CustomClaimService } from './custom-claim.service';
import { MemberOnboardingProgress } from './member-onboarding-progress.service';
import { OnboardingChecklistItem } from './onboarding.service';

// =============================================================================
// Interfaces
// =============================================================================

/**
 * Represents a single personal setup checklist item.
 * Sourced from the member onboarding progress API.
 */
export interface PersonalOnboardingItem {
  /** Display title for the checklist row */
  title: string;
  /** Brief description explaining why this step matters */
  description?: string;
  /** Whether this item has been completed */
  isCompleted: boolean;
  /** Whether this item is required for onboarding completion */
  isRequired: boolean;
  /** Whether to show an "Optional" badge */
  isOptional: boolean;
}

/**
 * Represents a single organization setup checklist item.
 * Sourced from the owner onboarding progress API.
 */
export interface OrgOnboardingItem {
  /** Unique key identifying the onboarding item */
  itemKey: string;
  /** Display title for the checklist row */
  title: string;
  /** Optional subtitle (e.g., cert types for "Upload Organization Certifications") */
  subtitle?: string;
  /** Whether this item has been completed */
  isCompleted: boolean;
  /** Whether this item is required for onboarding completion */
  isRequired: boolean;
  /** Whether to show an "Optional" badge */
  isOptional: boolean;
  /** Router path to navigate to when clicked */
  actionRoute?: string;
}

/**
 * Top-level state for the unified onboarding system.
 * Combines personal (member) and organization (owner) onboarding tracks.
 */
export interface UnifiedOnboardingState {
  /** List of personal setup items */
  personalItems: PersonalOnboardingItem[];
  /** List of organization setup items */
  orgItems: OrgOnboardingItem[];
  /** Combined completion percentage across both tracks (0-100) */
  combinedPercentage: number;
  /** Count of incomplete personal items (all, not just required) */
  personalIncompleteCount: number;
  /** Count of incomplete org items (all, not just required) */
  orgIncompleteCount: number;
  /** Whether the onboarding card has been dismissed by the user */
  isDismissed: boolean;
  /** Whether all required items across both tracks are complete */
  isFullyComplete: boolean;
  /** Whether data is currently loading */
  isLoading: boolean;
}

// =============================================================================
// API Response Interfaces
// =============================================================================

/**
 * API response shape for owner onboarding progress.
 * Matches: GET /organizations/:orgId/owner-onboarding/progress
 */
interface OwnerOnboardingProgressResponse {
  success: boolean;
  data?: {
    checklist: OnboardingChecklistItem[];
    stats: {
      completed: number;
      total: number;
      percentComplete: number;
      allRequiredComplete: boolean;
    };
    dismissed: boolean;
    completedAt: string | null;
  };
  error?: string;
}

/**
 * API response shape for member onboarding progress.
 * Matches: GET /organizations/:orgId/onboarding/progress
 */
interface MemberOnboardingProgressResponse {
  success: boolean;
  data?: {
    progress: MemberOnboardingProgress;
  };
  error?: string;
}

// =============================================================================
// Service
// =============================================================================

/**
 * Unified Onboarding Service
 *
 * Single source of truth for all onboarding state, replacing both
 * OnboardingService (BehaviorSubject-based) and MemberOnboardingProgressService
 * (promise-based) for the dashboard card and sidebar compact view.
 *
 * Uses Angular Signals for reactive state management.
 *
 * Reference: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class UnifiedOnboardingService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private organizationContext = inject(OrganizationContextService);
  private customClaimService = inject(CustomClaimService);
  private apiUrl = environment.apiUrl;

  // -------------------------------------------------------------------------
  // Writable Signals
  // -------------------------------------------------------------------------

  /** Raw member onboarding progress from API */
  readonly personalProgress = signal<MemberOnboardingProgress | null>(null);

  /** Raw owner onboarding checklist items from API */
  readonly orgChecklist = signal<OnboardingChecklistItem[]>([]);

  /** Whether the owner onboarding card has been dismissed */
  readonly orgDismissed = signal<boolean>(false);

  /** Whether the owner onboarding has a completedAt timestamp */
  readonly orgCompleted = signal<boolean>(false);

  /** Whether data is currently being fetched */
  readonly isLoading = signal<boolean>(false);

  /** Whether the current user is Owner or Chief Supervisor */
  readonly isOwnerOrChiefSupervisor = signal<boolean>(false);

  /** Whether progress data has been loaded at least once */
  readonly hasLoadedOnce = signal<boolean>(false);

  // -------------------------------------------------------------------------
  // Computed Signals
  // -------------------------------------------------------------------------

  /**
   * Personal onboarding items mapped from MemberOnboardingProgress.
   * Each field maps to a PersonalOnboardingItem with title, required status, and completion.
   */
  readonly personalItems = computed<PersonalOnboardingItem[]>(() => {
    const progress = this.personalProgress();
    if (!progress) return [];

    return [
      {
        title: 'Complete Profile',
        description: 'Name, phone, and address for your pilot account',
        isCompleted: progress.profileCompleted,
        isRequired: true,
        isOptional: false
      },
      {
        title: 'Upload Part 107 Certificate',
        description: 'Required for FAA-compliant drone operations',
        isCompleted: progress.part107Uploaded,
        isRequired: true,
        isOptional: false
      },
      {
        title: 'Upload FAA Medical Certificate',
        isCompleted: progress.medicalUploaded,
        isRequired: false,
        isOptional: true
      },
      {
        title: 'Upload Insurance Certificate',
        isCompleted: progress.insuranceUploaded,
        isRequired: false,
        isOptional: true
      },
      {
        title: 'Upload State Applicator License',
        description: 'Required for agricultural spray operations',
        isCompleted: progress.stateLicenseUploaded,
        isRequired: true,
        isOptional: false
      },
      {
        title: 'Review Safety Guidelines',
        description: 'FAA safety rules and operational best practices',
        isCompleted: progress.safetyGuidelinesReviewed,
        isRequired: true,
        isOptional: false
      }
    ];
  });

  /**
   * Organization onboarding items mapped from OnboardingChecklistItem[].
   * Filters to visible items and renames "Upload Certifications" to
   * "Upload Organization Certifications" with subtitle.
   */
  readonly orgItems = computed<OrgOnboardingItem[]>(() => {
    const checklist = this.orgChecklist();
    return checklist
      .filter(item => item.isVisible)
      .map(item => {
        const isUploadCerts = item.itemKey === 'upload_certifications';
        return {
          itemKey: item.itemKey,
          title: isUploadCerts ? 'Upload Organization Certifications' : item.title,
          subtitle: isUploadCerts ? 'Part 137, 44807, Liability Insurance, State Applicator License' : undefined,
          isCompleted: item.isCompleted,
          isRequired: item.isRequired,
          isOptional: !item.isRequired,
          actionRoute: item.actionRoute
        };
      });
  });

  /**
   * Count of incomplete personal items (all items, not just required).
   */
  readonly personalIncompleteCount = computed<number>(() => {
    return this.personalItems().filter(item => !item.isCompleted).length;
  });

  /**
   * Count of incomplete org items (all items, not just required).
   */
  readonly orgIncompleteCount = computed<number>(() => {
    return this.orgItems().filter(item => !item.isCompleted).length;
  });

  /**
   * Combined completion percentage across both tracks.
   *
   * For Owner/CS: denominator = 6 (4 required personal + 2 required org items)
   * For Member-only: denominator = 4 (4 required personal items only)
   *
   * Only required items count toward the percentage.
   */
  readonly combinedPercentage = computed<number>(() => {
    const personalItems = this.personalItems();
    if (personalItems.length === 0) return 0;

    // Count completed required personal items
    const completedPersonalRequired = personalItems
      .filter(item => item.isRequired && item.isCompleted)
      .length;

    if (this.isOwnerOrChiefSupervisor()) {
      // Count completed required org items
      const completedOrgRequired = this.orgItems()
        .filter(item => item.isRequired && item.isCompleted)
        .length;

      // 4 required personal + 2 required org = 6 total
      const totalRequired = 6;
      const completedRequired = completedPersonalRequired + completedOrgRequired;
      return Math.round((completedRequired / totalRequired) * 100);
    } else {
      // Member-only: 4 required personal items
      const totalRequired = 4;
      return Math.round((completedPersonalRequired / totalRequired) * 100);
    }
  });

  /**
   * Whether the card is dismissed.
   */
  readonly isDismissed = computed<boolean>(() => this.orgDismissed());

  /**
   * Whether all required items across both tracks are complete.
   */
  readonly isFullyComplete = computed<boolean>(() => {
    const personalItems = this.personalItems();
    if (personalItems.length === 0) return false;

    // All required personal items must be complete
    const allPersonalRequiredComplete = personalItems
      .filter(item => item.isRequired)
      .every(item => item.isCompleted);

    if (this.isOwnerOrChiefSupervisor()) {
      // All required org items must also be complete
      const allOrgRequiredComplete = this.orgItems()
        .filter(item => item.isRequired)
        .every(item => item.isCompleted);
      return allPersonalRequiredComplete && allOrgRequiredComplete;
    }

    return allPersonalRequiredComplete;
  });

  // -------------------------------------------------------------------------
  // Constructor -- auto-load on org context change
  // -------------------------------------------------------------------------

  constructor() {
    // Auto-load progress when organization context changes
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        this.loadProgress();
      }
    });
  }

  // -------------------------------------------------------------------------
  // Public Methods
  // -------------------------------------------------------------------------

  /**
   * Load onboarding progress from both API endpoints in parallel.
   * Calls member progress (always) and owner progress (may 403 for non-owners).
   */
  async loadProgress(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    try {
      this.isLoading.set(true);

      // Determine role synchronously
      const isOwnerOrCS = this.customClaimService.isOwnerOrChiefSupervisor();
      this.isOwnerOrChiefSupervisor.set(isOwnerOrCS);

      // Fetch both endpoints in parallel
      const [memberResult, ownerResult] = await Promise.all([
        this.fetchMemberProgress(orgId),
        isOwnerOrCS ? this.fetchOwnerProgress(orgId) : Promise.resolve(null)
      ]);

      // Update member progress signal
      if (memberResult) {
        this.personalProgress.set(memberResult);
      }

      // Update owner progress signals
      if (ownerResult) {
        this.orgChecklist.set(ownerResult.checklist);
        this.orgDismissed.set(ownerResult.dismissed);
        this.orgCompleted.set(ownerResult.completedAt !== null);
      }

      this.hasLoadedOnce.set(true);
    } catch (error) {
      console.error('Error loading unified onboarding progress:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Dismiss the unified onboarding card.
   * Updates UI instantly (optimistic), then persists via API in the background.
   */
  async dismiss(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    // Instant UI update
    this.orgDismissed.set(true);

    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${this.apiUrl}/organizations/${orgId}/owner-onboarding/dismiss`,
          {}
        )
      );
    } catch (error) {
      console.error('Error dismissing onboarding:', error);
      // Keep dismissed even on error -- user intent is clear
    }
  }

  /**
   * Restore the unified onboarding card from dismissed state.
   * Updates UI instantly (optimistic), then persists via API in the background.
   */
  async restore(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    // Instant UI update
    this.orgDismissed.set(false);

    // Navigate to dashboard if not already there
    if (!this.router.url.startsWith('/dashboard')) {
      this.router.navigate(['/dashboard']);
    }

    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${this.apiUrl}/organizations/${orgId}/owner-onboarding/show`,
          {}
        )
      );
    } catch (error) {
      console.error('Error restoring onboarding:', error);
      // Keep restored even on error -- user intent is clear
    }
  }

  /**
   * Refresh all progress data after wizard modal completes.
   * Re-calls loadProgress to update all signals reactively.
   */
  async refreshAfterWizardComplete(): Promise<void> {
    await this.loadProgress();
  }

  /**
   * Sync owner onboarding progress with actual data, then refresh signals.
   * The sync endpoint recalculates completion from live database state
   * (e.g., checks if org certs exist after a new upload).
   * Fire-and-forget safe — errors are logged but don't propagate.
   */
  async syncAndRefresh(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${this.apiUrl}/organizations/${orgId}/owner-onboarding/sync`,
          {}
        )
      );
    } catch (error) {
      console.error('Error syncing onboarding progress:', error);
    }

    await this.loadProgress();
  }

  // -------------------------------------------------------------------------
  // Private Methods
  // -------------------------------------------------------------------------

  /**
   * Fetch member onboarding progress from API.
   * GET /organizations/:orgId/onboarding/progress
   */
  private async fetchMemberProgress(orgId: string): Promise<MemberOnboardingProgress | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<MemberOnboardingProgressResponse>(
          `${this.apiUrl}/organizations/${orgId}/onboarding/progress`
        )
      );

      if (response.success && response.data) {
        return response.data.progress;
      }
      return null;
    } catch (error) {
      console.error('Error fetching member onboarding progress:', error);
      return null;
    }
  }

  /**
   * Fetch owner onboarding progress from API.
   * GET /organizations/:orgId/owner-onboarding/progress
   * May return 403 for non-owner users; this is handled gracefully.
   */
  private async fetchOwnerProgress(orgId: string): Promise<{
    checklist: OnboardingChecklistItem[];
    dismissed: boolean;
    completedAt: string | null;
  } | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<OwnerOnboardingProgressResponse>(
          `${this.apiUrl}/organizations/${orgId}/owner-onboarding/progress`
        )
      );

      if (response.success && response.data) {
        return {
          checklist: response.data.checklist,
          dismissed: response.data.dismissed,
          completedAt: response.data.completedAt
        };
      }
      return null;
    } catch (error: any) {
      // 403 is expected for non-owner users -- handle gracefully
      if (error?.status === 403) {
        return null;
      }
      console.error('Error fetching owner onboarding progress:', error);
      return null;
    }
  }
}
